import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import BreadcrumbContent from '@/components/atomic/templates/navigation/breadcrumbs/components/breadcrumb/content';
import BreadcrumbLink from '@/components/atomic/templates/navigation/breadcrumbs/components/breadcrumb/link';
import BreadcrumbPrevLink from '@/components/atomic/templates/navigation/breadcrumbs/components/breadcrumb/prev-link';
import withExposer from '@/hocs/with-exposer';
import useStore from '@/state';
import closeNavigationAction from '@/state/selectors/navigation/close-navigation';
import getIsLastItemWithSubNavList from '@/state/selectors/secondary-nav/get-is-last-item-with-subnav-list';
import isBreadcrumbIndexOpen from '@/state/selectors/secondary-nav/is-breadcrumb-index-open';
import setBreadcrumbIndexAction from '@/state/selectors/secondary-nav/set-breadcrumb-index';
import { SecondaryNavLinkHandler } from '@/state/slices/secondary-nav/typings';
import { BasicComponentProps } from '@/typings/generic';

interface BreadcrumbProps extends BasicComponentProps, SecondaryNavLinkHandler {
	index: number;
	isFirstItem: boolean;
	isLastItem: boolean;
}

const BreadcrumbComponent: React.FC<BreadcrumbProps> = ({
	$exposerSuffix,
	title,
	previousLink,
	subNavList,
	index,
	hidePreviousLink,
	isFirstItem,
	isLastItem,
}) => {
	const rootElement = useRef<HTMLDivElement>(null);

	const closeNavigation = useStore(closeNavigationAction);
	const setBreadcrumbIndex = useStore(setBreadcrumbIndexAction);

	const dropdownOpen = useStore((state) =>
		isBreadcrumbIndexOpen(state, index),
	);

	const toggleDropdown = useCallback(
		() => setBreadcrumbIndex(dropdownOpen ? null : index),
		[dropdownOpen, index, setBreadcrumbIndex],
	);

	const onEscClick = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') setBreadcrumbIndex(null);
		},
		[setBreadcrumbIndex],
	);

	const isLastItemWithSubNavList = useStore((state) =>
		getIsLastItemWithSubNavList(state, index),
	);

	useEffect(() => {
		if (dropdownOpen) {
			globalThis.addEventListener('keydown', onEscClick);
		} else {
			globalThis.removeEventListener('keydown', onEscClick);
		}

		return () => {
			globalThis.removeEventListener('keydown', onEscClick);
		};
	}, [dropdownOpen, onEscClick]);

	const hasDropdown = useMemo(
		() => !!(subNavList && subNavList.length > 0),
		[subNavList],
	);

	const className = useMemo(() => {
		const classes = ['dxnav__breadcrumb'];
		if (hasDropdown) classes.push('dxnav__breadcrumb--with-dropdown');
		if (dropdownOpen) classes.push('dxnav-active');
		if (isLastItemWithSubNavList) classes.push('dxnav-mobile-active');
		return classes.join(' ');
	}, [dropdownOpen, hasDropdown, isLastItemWithSubNavList]);

	useEffect(() => {
		if (dropdownOpen) closeNavigation();
	}, [closeNavigation, dropdownOpen]);

	if (!title) return null;

	return (
		<div className={className} ref={rootElement}>
			<BreadcrumbLink
				$exposerSuffix={$exposerSuffix}
				title={title}
				dropdownOpen={dropdownOpen}
				toggleDropdown={toggleDropdown}
				hasDropdown={hasDropdown}
				index={index}
			/>
			<BreadcrumbContent
				subNavList={subNavList}
				dropdownOpen={dropdownOpen}
				index={index}
			/>
			{!hidePreviousLink && (!isFirstItem || !isLastItem) ? (
				<BreadcrumbPrevLink
					$exposerSuffix={$exposerSuffix}
					previousLink={previousLink}
				/>
			) : null}
		</div>
	);
};

const Breadcrumb = memo(withExposer(BreadcrumbComponent, 'breadcrumb'));

export default Breadcrumb;
